import { EventName } from './events/eventsName';
import { GameEvent } from './events/gameEvent';
import { SolitaireGame } from './SolitaireGame';
import { Store } from './store/gameStore';
import { SoundManager } from './core/soundManager';
import { GameName } from 'constants/difficulty';
import gsap from 'gsap';

import { findKlondikeMagic } from './gameMagicWand/KlondikeMagic';
import { findSpiderMagic } from './gameMagicWand/SpiderMagic';
import { MagicItem } from './gameMagicWand/magicGroup';
import { Sprite } from 'pixi.js';
import { findFreeCellMagic } from './gameMagicWand/FreeCellMagic';
import { GAEvent, GAEventNames } from './analytics/events';
import { Card } from 'components/Card';
import { getAsset, onAssetsLoad } from './preloadGameAssets';
import { addToLevel, fadeUI } from './core/Playable2DRootContainer';
import { getScreenSize } from './core/screen';
import { Layout } from './LayoutManager';
import { MonoBehavior } from './core/monoBehavior';
import { MagicUIParticles } from './MagicUIParticles';
import { MagicGlow } from './MagicGlow';
import { MagicOverlay } from './MagicOverlay';
import { isMagicNeeded } from './gameMagicWand/isMagicNeeded';
import { GamePlace } from 'constants/cards';
import { KingPopup } from './KingPopup';

export class MagicWand extends MonoBehavior {
    game: SolitaireGame;
    active = false;
    magicWandSprite?: Sprite;
    activeParticle = false;
    magicCard: Card | null = null;
    constructor(game: SolitaireGame) {
        super();

        this.game = game;
        this.magicWand = this.magicWand.bind(this);
        GameEvent.add(EventName.MAGIC_WAND, this.magicWand);
        GameEvent.add(EventName.CLOSE_MODAL, this.modalClosed.bind(this));
        onAssetsLoad(this.preloadMagicWand.bind(this));
    }
    modalClosed({ id, confirm }: { id: string; confirm: boolean }) {
        if (id === 'magic-wand' && !confirm) {
            GAEvent(GAEventNames.MAGIC_WAND_ONBOARDING_CLOSE, {
                id: Store.gameId,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
            });
        }
    }

    isAvailable() {
        GameEvent.emit(EventName.BLOCK_MAGIC_WAND, !isMagicNeeded(this.game));
    }
    magicWand() {
        Store.startGame();
        if (!isMagicNeeded(this.game)) {
            GameEvent.emit(EventName.NO_MAGIC_KING_INFO);
            new KingPopup('noMagicNeeded');
            return;
        }

        // if (!Store.hasPopup('magic-wand')) {
        //     showPopup('magic-wand');
        //     GAEvent(GAEventNames.MAGIC_WAND_ONBOARDING, {
        //         id: Store.gameId,
        //         type: Store.getGameTypeText(),
        //         difficulty: Store.getDifficultyText(),
        //     });
        //     return;
        // }

        let magicItem: MagicItem | null;

        switch (Store.name) {
            case GameName.KLONDIKE:
                magicItem = findKlondikeMagic(this.game);
                break;
            case GameName.SPIDER:
                magicItem = findSpiderMagic(this.game);
                break;
            case GameName.FREECELL:
                magicItem = findFreeCellMagic(this.game);
                break;
        }

        if (magicItem) this.makeMagic(magicItem);
        else this.showNoMagic();
    }

    showNoMagic() {
        // showPopup('out-move-wand');
        // GAEvent(GAEventNames.SHOW_NO_HINT_MODAL, {
        //     moves: Store.movesCount,
        //     id: Store.gameId,
        //     type: Store.getGameTypeText(),
        //     difficulty: Store.getDifficultyText(),
        // });
    }

    async preloadMagicWand() {
        const magicWandImage = await getAsset('MagicWand/magicWand');
        this.magicWandSprite = Sprite.from(magicWandImage);
        this.magicWandSprite.anchor.set(0.5, 0.3);
        this.magicWandSprite.zIndex = 10000;
        this.magicWandSprite.visible = false;
        addToLevel(this.magicWandSprite);
    }
    playMagicWandAnimation(moveCard: Card, callback: () => void) {
        if (!this.magicWandSprite) return;
        this.activeParticle = true;

        const fadeInTime = 0.7;
        const hitTime = 0.5;
        const fadeOutTime = 1.2;
        const { width, height } = getScreenSize();

        const cardSize = Layout.getCardSize();
        this.magicWandSprite.width = cardSize.width * 1.5;
        this.magicWandSprite.height = this.magicWandSprite.width;
        const targetPosition = moveCard.container.position.clone();
        const touchPoint = moveCard.place.cardPlace === GamePlace.TABLEAU ? cardSize.height * 0.3 : 0;
        this.magicWandSprite.position.set(0, height * 0.6);
        this.magicWandSprite.visible = true;
        this.magicWandSprite.alpha = 1;

        gsap.to(this.magicWandSprite, { rotation: 1, duration: fadeInTime, ease: 'power1.easeOut' });

        gsap.to(this.magicWandSprite.position, {
            x: targetPosition.x - this.magicWandSprite.width * 0.1,
            y: targetPosition.y - cardSize.height * 1.0,
            duration: fadeInTime,
            ease: 'power1.easeOut',
        }).then(() => {
            if (!this.magicWandSprite) return;
            gsap.to(this.magicWandSprite, { rotation: 1.6, duration: hitTime, ease: 'power1.easeOut' });
            gsap.to(this.magicWandSprite.position, {
                x: targetPosition.x - this.magicWandSprite.width * 0.1,
                y: targetPosition.y - touchPoint - cardSize.height * 0.16,
                duration: hitTime,
                ease: 'power1.easeInOut',
            }).then(() => {
                callback();
                if (!this.magicWandSprite) return;
                this.activeParticle = false;
                gsap.to(this.magicWandSprite.position, {
                    x: targetPosition.x - this.magicWandSprite.width * 2.7,
                    y: targetPosition.y,
                    duration: fadeOutTime,
                    ease: 'power1.easeIn',
                });
                gsap.to(this.magicWandSprite, {
                    alpha: 0,
                    rotation: 0.1,
                    duration: fadeOutTime,
                    ease: 'power1.easeIn',
                });
            });
            gsap.to(this.magicWandSprite, {
                width: this.magicWandSprite.width * 0.92,
                height: this.magicWandSprite.width * 0.92,
                duration: hitTime,
                ease: 'power1.easeInOut',
                yoyo: true,
                repeat: 1,
            });

            gsap.to(this.magicWandSprite, {
                rotation: 2,
                duration: hitTime,
                ease: 'power1.easeInOut',
                yoyo: true,
                repeat: 1,
            });
        });
    }
    makeMagic(magicItem: MagicItem) {
        this.active = true;
        Store.magicWandUsed++;
        this.game.blockMove(Infinity);
        Store.setWandCounter(Store.wandCounter - 1);
        GameEvent.emit(EventName.WAND_SHOW);
        SoundManager.play('magicWandActionSFX');
        let { moveCard } = magicItem;
        this.magicCard = moveCard;
        new MagicGlow(this.magicCard);
        new MagicOverlay(this.magicCard);
        fadeUI(0.6);

        this.playMagicWandAnimation(moveCard, () => {
            moveCard.takeUp();
            this.game.moves.add([moveCard]);

            const center = Layout.getBoardCenter();
            gsap.to(moveCard.container.position, { x: center.x, y: center.y, duration: 0.6 }).then(() => {
                moveCard.setPosition(center);

                if (!moveCard.visible) {
                    if (moveCard.place.cardPlace === GamePlace.TABLEAU) {
                        GameEvent.emit(EventName.REVEAL_CARD, moveCard);
                        //revealCard in column, it will be added score in Klondike
                    }
                    moveCard.setVisible(true);
                }
                moveCard.setActive(true);
                this.game.removeCardFromBoard(moveCard);
                this.game.refreshBoard();
                this.magicCard?.cardDisplay.glowMagic();
                this.magicCard = null;
            });
            gsap.to(moveCard.cardDisplay.container.scale, { x: 1.2, y: 1.2, duration: 0.3, delay: 0.7 }).then(() => {
                gsap.to(moveCard.cardDisplay.container.scale, { x: 1, y: 1, duration: 0.3 }).then((x) => {
                    this.game.dropMagicCard(magicItem);
                    fadeUI(1);
                });
            });
        });
    }
    update() {
        if (this.activeParticle && this.magicWandSprite) {
            new MagicUIParticles(this.magicWandSprite, 2);
            if (this.magicCard) this.magicCard.cardDisplay.showMagic();
        }
    }
}
